import clsx from "clsx"
import { useEffect, useState } from "react"

import { Button, CheckIcon, Dialog, SectionHeading } from "components"
import CoversStepper from "./CoversStepper"
import { DEFAULT_COVERS } from "presenters/PlannerPresenter"
import { normaliseEmail } from "@/email"
import type { PlanningSession, SessionInvite, SessionMember, UserProfile } from "@/types"

interface SessionSheetProps {
  open: boolean
  onClose: () => void
  sessions: PlanningSession[]
  current: PlanningSession | null
  me: SessionMember | null
  /** Everyone with an account, so a session can ask them in. */
  people: UserProfile[]
  /** Asked in and not yet answered — see `useAskedIn`. */
  asked: SessionInvite[]
  onPick: (sessionId: string) => void
  onStart: (name: string, covers: number) => void
  onInvite: (email: string) => void
  /** Takes another member out. Offered to the owner alone — see `iOwnThis`. */
  onRemove: (uid: string) => void
  onLeave: () => void
  onDelete: () => void
  /** Whoever started it deletes; everybody else leaves. */
  iOwnThis: boolean
  /** What deletion would take with it, so the confirmation can say. */
  plannedCount: number
  itemCount: number
  isBusy: boolean
}

const plural = (n: number, noun: string) => `${n} ${noun}${n === 1 ? "" : "s"}`

const initialsOf = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("") || "?"

const Person = ({
  name,
  detail,
  meta,
  dashed = false,
  action,
}: {
  name: string
  /** Only supplied when the name alone does not say who this is — see `matches`. */
  detail?: string
  meta?: string
  dashed?: boolean
  action?: React.ReactNode
}) => (
  <li className='flex min-h-[54px] items-center gap-3 border-b border-ink/10 py-2 last:border-b-0'>
    <span
      className={clsx(
        "grid h-9 w-9 shrink-0 place-items-center border font-heading text-[13px] font-semibold",
        dashed ? "border-dashed border-divider text-muted" : "border-steel text-steel-700"
      )}>
      {initialsOf(name)}
    </span>
    <span className='min-w-0 flex-1'>
      <span className='block truncate text-[16px]'>{name}</span>
      {detail && <span className='block truncate text-[13px] text-muted'>{detail}</span>}
    </span>
    {meta && <span className='shrink-0 font-mono text-[11px] text-muted'>{meta}</span>}
    {action}
  </li>
)

/** Long enough to reach with a thumb, short enough not to sit armed. */
const CONFIRM_MS = 4000

/**
 * Takes a member out, in two taps in one place.
 *
 * The same gesture `<ChangeMark>` uses for a revert, and for the same reason:
 * the confirmation lands under the finger that is already there, so no dialog
 * opens over a list whose whole job is showing who is in. It disarms itself
 * after a few seconds, because a primed "Sure?" left sitting beside somebody's
 * name is one stray tap from removing them.
 *
 * Not the sheet's `Delete`/`Leave` panel, which is deliberately heavier: that
 * one destroys a week, and this is undoable at the cost of one ask.
 */
const RemovePerson = ({
  name,
  onRemove,
  disabled,
}: {
  name: string
  onRemove: () => void
  disabled: boolean
}) => {
  const [armed, setArmed] = useState(false)

  useEffect(() => {
    if (!armed) return
    const timer = setTimeout(() => setArmed(false), CONFIRM_MS)
    return () => clearTimeout(timer)
  }, [armed])

  return (
    <Button
      danger
      variant={armed ? "primary" : "secondary"}
      disabled={disabled}
      onClick={() => {
        if (!armed) return setArmed(true)
        setArmed(false)
        onRemove()
      }}
      aria-label={armed ? `Confirm taking ${name} out` : `Take ${name} out of this session`}
      // One width for both labels: the button sits at the end of a row whose
      // name takes the rest, and a chip that resizes when armed shunts the name
      // beside it.
      className='mt-0 mr-0 min-w-[92px] shrink-0'>
      {armed ? "Sure?" : "Remove"}
    </Button>
  )
}

/**
 * Switching between the sessions somebody is in, and starting or leaving one.
 *
 * A sheet from the bottom rather than a page: switching session is not
 * navigation — the Plan tab is the same tab either way — and coming back to
 * exactly the week you were looking at is the point. It doubles as the members
 * screen because "who is in this" and "which one am I in" are the same question
 * asked from two directions.
 */
const SessionSheet = ({
  open,
  onClose,
  sessions,
  current,
  me,
  people,
  asked,
  onPick,
  onStart,
  onInvite,
  onRemove,
  onLeave,
  onDelete,
  iOwnThis,
  plannedCount,
  itemCount,
  isBusy,
}: SessionSheetProps) => {
  const [name, setName] = useState("")
  const [covers, setCovers] = useState(DEFAULT_COVERS)
  const [ending, setEnding] = useState(false)
  const [search, setSearch] = useState("")

  // Disarm when the sheet closes or the session changes under it — a primed
  // "Delete it" must never be left sitting over a different session's name,
  // and a half-typed search belongs to the session it was typed in.
  useEffect(() => {
    setEnding(false)
    setSearch("")
  }, [open, current?.id])

  const start = () => {
    if (name.trim() === "") return
    onStart(name, covers)
    setName("")
    setCovers(DEFAULT_COVERS)
  }

  /**
   * Compared normalised throughout. Profiles are keyed by the normalised
   * address now, but a session's `members` and an invite's `toEmail` are
   * strings written at various times by various paths, and one that predates
   * that keying would otherwise be filtered out of nothing and offered again.
   */
  const askedAlready = new Set(asked.map((invite) => normaliseEmail(invite.toEmail)))
  const inSession = new Set((current?.members ?? []).map((m) => normaliseEmail(m.email ?? "")))

  const invitable = people.filter((person) => {
    const email = normaliseEmail(person.email)
    return (
      email !== normaliseEmail(me?.email ?? " ") &&
      !inSession.has(email) &&
      // Someone already asked is not offered again. Asking twice is harmless —
      // the id makes it a replace, not a stack — but a button that looks like
      // it does something and provably does nothing is worse than no button.
      !askedAlready.has(email)
    )
  })

  /** The asks, matched back to a profile so they can be shown as people. */
  const pending = asked.map((invite) => {
    const profile = people.find(
      (person) => normaliseEmail(person.email) === normaliseEmail(invite.toEmail)
    )
    const named = `${profile?.firstName ?? ""} ${profile?.lastName ?? ""}`.trim()
    return { id: invite.id, name: named || invite.toEmail, email: invite.toEmail, named }
  })

  /**
   * Matched on name *and* address, because a household knows each other by
   * first name but an account may carry only an email — and "b" should find
   * "B W" as readily as "bheadwhite@…". Capped so the sheet cannot grow a
   * scrolling roster by another route.
   *
   * **Every row carries its address under the name.** One profile per address
   * is an invariant (`users/{email}`, pinned in the rules), but one profile per
   * *name* is not, and must not be: two people can be called the same thing and
   * both belong in this list. An invite sent to the wrong one of two identical
   * rows reaches a real person who is not the one you meant, which is worse
   * than the ask failing — and the address is what the invite is actually
   * addressed to, so showing it is showing what the button will do.
   */
  const matches = (() => {
    const needle = search.trim().toLowerCase()
    if (needle === "") return []
    return invitable
      .filter((person) =>
        `${person.firstName} ${person.lastName} ${person.email}`.toLowerCase().includes(needle)
      )
      .slice(0, 6)
  })()

  return (
    <Dialog open={open} onClose={onClose} title='Planning sessions'>
      <p className='-mt-1 mb-2 text-muted'>Each one keeps its own week and list.</p>

      {sessions.length === 0 ? (
        <p className='py-2'>
          You're not in any session yet. Start one below, or take an ask from your own tab.
        </p>
      ) : (
        <ul className='-mx-1'>
          {sessions.map((session) => {
            const isCurrent = session.id === current?.id
            return (
              <li key={session.id} className='border-b border-ink/10 last:border-b-0'>
                <button
                  type='button'
                  onClick={() => {
                    onPick(session.id)
                    onClose()
                  }}
                  className={clsx(
                    "flex w-full cursor-pointer touch-manipulation items-center gap-3 px-1 py-3 text-left",
                    isCurrent ? "bg-steel-100" : "hover:bg-ink/5"
                  )}>
                  <span className='min-w-0 flex-1'>
                    <span className='block truncate font-heading text-[19px] font-semibold'>
                      {session.name}
                    </span>
                    <span className='block truncate text-[13px] text-muted'>
                      {session.members.length === 1
                        ? "just you"
                        : `${session.members.length} people`}{" "}
                      · cooking for {session.covers}
                    </span>
                  </span>
                  {isCurrent && <CheckIcon className='h-5 w-5 shrink-0 text-steel' />}
                </button>
              </li>
            )
          })}
        </ul>
      )}

      <SectionHeading>Start a new one</SectionHeading>
      <input
        value={name}
        onChange={(event) => setName(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") start()
        }}
        placeholder='Weeknights, Sunday supper…'
        aria-label='Name for the new session'
        className='mt-2 h-12 w-full border border-divider bg-surface px-3 text-base hover:border-ink/45 focus-visible:border-steel focus-visible:outline-offset-0'
      />
      <div className='mt-2 flex items-center gap-2'>
        <CoversStepper value={covers} onChange={setCovers} noun='at the table' />
        <Button
          variant='primary'
          onClick={start}
          disabled={isBusy || name.trim() === ""}
          className='mt-0 mr-0 h-11 flex-1'>
          Start
        </Button>
      </div>
      <p className='mt-2 text-[13px] leading-snug text-muted'>
        Starts empty with you in it, cooking for {covers}. Ask people in once it exists.
      </p>

      {current != null && (
        <>
          <SectionHeading
            meta={`${current.members.length} in${
              pending.length > 0 ? ` · ${pending.length} asked` : ""
            }`}>
            In this session
          </SectionHeading>
          <ul>
            {current.members.map((member) => {
              const started = member.uid === current.ownerUid
              return (
                <Person
                  // Keyed by session too, so switching remounts the row and no
                  // primed "Sure?" is left sitting over a different week's
                  // roster — the same disarming the sheet does for its own
                  // delete panel.
                  key={`${current.id}:${member.uid}`}
                  name={member.uid === me?.uid ? `${member.name} (you)` : member.name}
                  meta={started ? "started it" : undefined}
                  // Only whoever started it, and never on themselves: the owner's
                  // way out is deleting the session, and stepping out of one
                  // would leave it standing with nobody able to read or remove
                  // it. `firestore.rules` says the same, and is what decides it.
                  action={
                    iOwnThis && !started ? (
                      <RemovePerson
                        name={member.name}
                        onRemove={() => onRemove(member.uid)}
                        disabled={isBusy}
                      />
                    ) : undefined
                  }
                />
              )
            })}
            {/* Asks sit under the members rather than in a section of their own:
             *  the question this list answers is "who is in on this week", and
             *  somebody halfway in is an answer to it. Dashed initials and the
             *  "asked" tag are what separate them — the same dashed treatment
             *  the picker's rows wear, meaning the same thing both times: not
             *  in yet. A heading and a second list would say they were a
             *  different kind of thing, and make the sheet longer to say it. */}
            {pending.map((invite) => (
              <Person
                key={invite.id}
                name={invite.name}
                detail={invite.named ? invite.email : undefined}
                dashed
                meta='asked'
              />
            ))}
          </ul>

          {invitable.length > 0 && (
            <>
              <SectionHeading>Ask someone in</SectionHeading>
              {/* Type to narrow rather than listing everybody: the roster grows
               *  with the app and a session sheet is not the place to scroll
               *  past it. Nothing is offered until something is typed, so this
               *  costs three lines however many accounts exist. */}
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder='Start typing a name or email…'
                aria-label='Search people to ask in by name or email'
                type='search'
                autoCapitalize='none'
                autoCorrect='off'
                spellCheck={false}
                className='mt-2 h-11 w-full border border-divider bg-surface px-3 text-base hover:border-ink/45 focus-visible:border-steel focus-visible:outline-offset-0'
              />

              {search.trim() !== "" && (
                <ul className='mt-1 max-h-[184px] overflow-y-auto'>
                  {matches.length === 0 ? (
                    <li className='py-3 text-[13px] text-muted'>
                      Nobody here matches "{search.trim()}". They need an account before
                      they can be asked in.
                    </li>
                  ) : (
                    matches.map((person) => {
                      const named = `${person.firstName} ${person.lastName}`.trim()
                      return (
                        <Person
                          key={person.email}
                          name={named || person.email}
                          // Not repeated underneath when it is already standing
                          // in for a missing name.
                          detail={named ? person.email : undefined}
                          dashed
                          action={
                            <Button
                              onClick={() => {
                                onInvite(person.email)
                                setSearch("")
                              }}
                              className='mt-0 mr-0'
                              // The name leads because that is who the person
                              // is, and the address follows because two people
                              // with one name would otherwise give two
                              // identical buttons to anyone not looking at the
                              // screen.
                              aria-label={`Ask ${
                                named ? `${named} (${person.email})` : person.email
                              } into ${current.name}`}>
                              Invite
                            </Button>
                          }
                        />
                      )
                    })
                  )}
                </ul>
              )}

              <p className='mt-2 text-[13px] leading-snug text-muted'>
                The ask lands on their own tab and waits there until they take it. Once
                they're in they see this week and the shopping list — never your other
                sessions.
              </p>
            </>
          )}

          {/* Two different endings, and they are not the same act. Whoever
           *  started a session can delete it, taking the week and the list with
           *  it; everybody else leaves, which takes them out without taking
           *  anything away from the people still cooking it. */}
          {ending ? (
            <div className='mt-6 border border-danger/40 bg-danger-100 p-3'>
              <p className='font-heading text-[17px] font-semibold text-danger'>
                {iOwnThis ? `Delete ${current.name}?` : `Leave ${current.name}?`}
              </p>
              <p className='mt-1.5 text-sm'>
                {iOwnThis ? (
                  <>
                    This deletes the session for everyone in it —{" "}
                    <strong>
                      {plural(plannedCount, "planned meal")} and{" "}
                      {plural(itemCount, "item")} on the shopping list
                    </strong>
                    , plus any asks still waiting. It cannot be undone. Your recipes are
                    not touched.
                  </>
                ) : (
                  <>
                    The session carries on without you, and you'd need another ask to get
                    back in. Nothing is deleted.
                  </>
                )}
              </p>
              <div className='mt-3 grid grid-cols-2 gap-2'>
                <Button onClick={() => setEnding(false)} className='mt-0 mr-0 bg-ground'>
                  Keep it
                </Button>
                <Button
                  danger
                  variant='primary'
                  disabled={isBusy}
                  onClick={() => {
                    setEnding(false)
                    if (iOwnThis) onDelete()
                    else onLeave()
                  }}
                  className='mt-0 mr-0'>
                  {iOwnThis ? "Delete it" : "Leave"}
                </Button>
              </div>
            </div>
          ) : (
            <Button danger onClick={() => setEnding(true)} className='mt-6 w-full border-dashed'>
              {iOwnThis ? `Delete ${current.name}` : `Leave ${current.name}`}
            </Button>
          )}
        </>
      )}
    </Dialog>
  )
}

export default SessionSheet
